import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';

// Store
import AppState from '@webapp/store/state';

// Translate
import { Translate } from 'react-localize-redux';

// Styled
import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

// Components
import RosterArtistItem from './roster-artist-item';

import Flex from '@components/layout/flex';
import Avatar from '@components/basics/avatar';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import RightArrowIcon from '@components/basics/icons/right-arrow';
import { RosterRelation } from '@services/rosters/types/roster-relation';
import { NewRosterRelation } from '@services/rosters/types/new-roster-relation';
import {
  AttachToRoster,
  DeleteRoster,
} from '@webapp/store/rosters/action-creators';
import { Dispatch } from 'redux';
import SimpleArtist from '@common/types/artist/simple-artist';

interface RosterMenuContentProps {
  artists: SimpleArtist[];
  isNewRoster?: boolean;
  isMobile?: boolean;
  rosterId?: string;
  activelabelId?: string;
  rosterName?: string;
  rosterArtists?: RosterRelation[];
  setRosterName: (rosterName: string) => void;
  showError: boolean;
  setShowError: (error: boolean) => void;
  removeRosterArtist: (relation: RosterRelation[]) => void;
  addExistingRosterArtist: (relation: SimpleArtist[]) => void;
  addNewRosterArtists: (artists: SimpleArtist[]) => void;
  removeRoster: (rosterId: string, labelId: string) => DeleteRoster;
  onMenuClose: () => void;
  formatter: (value: string) => string;
}

const mapStateToProps = (state: AppState) => {
  const activeLabelId = state.recordLabels.active?.id;
  return {
    isMobile: state.ui.mobile,
    artists: activeLabelId
      ? state.recordLabels.artists[activeLabelId]
          .map((r) => state.artist.myArtists.filter((a) => a.id === r.artistId))
          .flat()
      : [],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  addRosterArtist: (relation: NewRosterRelation) =>
    dispatch(new AttachToRoster(relation)),
  removeRoster: (rosterId: string, labelId: string) =>
    dispatch(new DeleteRoster(rosterId, labelId)),
});

const RosterMenuContent: FunctionComponent<RosterMenuContentProps> = ({
  isNewRoster,
  rosterId,
  activelabelId,
  rosterName,
  setRosterName,
  rosterArtists,
  artists,
  isMobile,
  addExistingRosterArtist,
  addNewRosterArtists,
  removeRosterArtist,
  removeRoster,
  onMenuClose,
}) => {
  const theme = useTheme();

  const [newRosterArtists, setNewRosterArtists] = React.useState<
    SimpleArtist[]
  >([]);

  const [newArtistsForRoster, setNewArtistsForRoster] = React.useState<
    SimpleArtist[]
  >([]);

  const [artistsToRemove, setArtistsToRemove] = React.useState<
    RosterRelation[]
  >([]);

  const handleAddArtist = (addArtist: SimpleArtist) => {
    if (!rosterId) {
      setNewRosterArtists([...newRosterArtists, addArtist]);
      addNewRosterArtists([...newRosterArtists, addArtist]);
    }

    if (rosterId && rosterName) {
      setNewArtistsForRoster([...newArtistsForRoster, addArtist]);
      addExistingRosterArtist([...newArtistsForRoster, addArtist]);
    }
  };

  const handleRemoveArtist = (removeArtist: RosterRelation) => {
    setArtistsToRemove([...artistsToRemove, removeArtist]);
    removeRosterArtist([...artistsToRemove, removeArtist]);
  };

  const handleDeleteRoster = () => {
    if (rosterId && activelabelId) {
      onMenuClose();
      removeRoster(rosterId, activelabelId);
    }
  };

  return (
    <Content>
      {!!rosterName && !isNewRoster && (
        <Typography type="analytics-nav" customFont="semi-bold">
          <Translate
            id={'USER_PROFILE.ROSTER.MENU.MANAGE.TITLE'}
            data={{
              rosterName:
                rosterName && rosterName.length > (isMobile ? 20 : 10)
                  ? `${rosterName.slice(0, isMobile ? 20 : 10)}...`
                  : rosterName,
            }}
          />
        </Typography>
      )}
      {isNewRoster && (
        <Typography type="analytics-nav" customFont="semi-bold">
          <Translate id={'USER_PROFILE.ROSTER.MENU.NEW.TITLE'} />
        </Typography>
      )}
      <Separator />
      <div>
        <InputField
          className="mb-30"
          isGrey
          label={<Translate id="USER_PROFILE.ROSTER.MENU.NAME.INPUT" />}
          value={rosterName}
          onChange={setRosterName}
        />
        <MotionOpacity>
          {
            <>
              <Typography
                className="mb-20"
                type="subheader"
                customFont="semi-bold"
              >
                {isNewRoster ? (
                  <Translate id="USER_PROFILE.ROSTER.MENU.NEW.ADD.TITLE" />
                ) : (
                  <Translate id="USER_PROFILE.ROSTER.MENU.MANAGE_ARTISTS.TITLE" />
                )}
              </Typography>
              <Typography
                color={theme.colors.tertiary}
                style={{ marginBottom: 10 }}
              >
                <Translate id="USER_PROFILE.ROSTER.MENU.MANAGE.TEXT" />
              </Typography>
            </>
          }
          <Typography className="helper mb-20" weight="700">
            <Translate
              id="USER_PROFILE.ROSTER.CONNECTED.TITLE"
              data={{ amount: artists.length || 0 }}
            />
          </Typography>
          <List>
            {artists
              .filter(
                (a) =>
                  !(rosterArtists || [])?.some((r) => r.artistId === a.id) &&
                  !newRosterArtists.some((r) => r.id === a.id)
              )
              .map((artist) => (
                <RosterArtistItem
                  key={artist.id}
                  name={artist.name}
                  avatar={artist.avatar}
                  onClick={() => handleAddArtist(artist)}
                />
              ))
              .concat(
                artistsToRemove.map((artist) => (
                  <RosterArtistItem
                    key={artist.artistId}
                    name={artist.artistName}
                    avatar={artist.artistAvatar}
                    onClick={() =>
                      setArtistsToRemove(
                        artistsToRemove.filter(
                          (artistToRemove) =>
                            artistToRemove.artistId !== artist.artistId
                        )
                      )
                    }
                  />
                ))
              )}
          </List>
          <>
            <Separator />
            <Flex
              className="mb-20"
              justifyContent="flex-start"
              alignItems="center"
              gap={10}
            >
              <Avatar className="label-avatar" />
              <Typography className="helper" weight="700">
                {rosterName || 'New Roster'} (
                {rosterArtists?.filter(
                  (a) => !artistsToRemove.some((art) => art.id === a.id)
                )?.length || newRosterArtists.length}
                )
              </Typography>
            </Flex>
            <List>
              {rosterId
                ? rosterArtists
                    ?.concat(artistsToRemove)
                    ?.filter(
                      (a) => !artistsToRemove.some((art) => art.id === a.id)
                    )
                    ?.map((artist) => (
                      <RosterArtistItem
                        key={artist.artistId}
                        name={artist.artistName}
                        avatar={artist.artistAvatar}
                        selected={true}
                        onClick={() => handleRemoveArtist(artist)}
                      />
                    ))
                    .concat(
                      newArtistsForRoster.map((artist) => (
                        <RosterArtistItem
                          key={artist.id}
                          name={artist.name}
                          avatar={artist.avatar}
                          selected={true}
                          onClick={() =>
                            setNewArtistsForRoster(
                              newArtistsForRoster.filter(
                                (newArtist) => newArtist.id !== artist.id
                              )
                            )
                          }
                        />
                      ))
                    )
                : newRosterArtists
                    .map((artist) => (
                      <RosterArtistItem
                        key={artist.id}
                        name={artist.name}
                        avatar={artist.avatar}
                        selected={true}
                        onClick={() => {
                          setNewRosterArtists(
                            newRosterArtists.filter(
                              (newArtist) => newArtist.id !== artist.id
                            )
                          );
                        }}
                      />
                    ))
                    .concat(
                      artistsToRemove.map((artist) => (
                        <RosterArtistItem
                          key={artist.artistId}
                          name={artist.artistName}
                          avatar={artist.artistAvatar}
                          selected={true}
                          onClick={() =>
                            setNewArtistsForRoster(
                              newArtistsForRoster.filter(
                                (newArtist) => newArtist.id !== artist.id
                              )
                            )
                          }
                        />
                      ))
                    )}
            </List>
          </>
        </MotionOpacity>
      </div>
      {!isNewRoster && (
        <StyledDeleteButton className="mt-30">
          <Label weight="700">
            <Translate id="USER_PROFILE.ROSTER.MENU.DELETE" />
          </Label>
          <ArrowWrapper onClick={handleDeleteRoster}>
            <RightArrowIcon className="icon" />
          </ArrowWrapper>
        </StyledDeleteButton>
      )}
    </Content>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RosterMenuContent);

const Content = styled.div`
  height: auto;
  display: flex;
  padding: 0px 50px 20px 30px;
  flex-direction: column;
  justify-content: space-between;

  .helper {
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .label-avatar {
    width: 32px;
    height: 32px;
  }
`;

const List = styled.div`
  display: grid;
  grid-gap: 15px;
`;

const Separator = styled.span`
  display: block;
  height: 1px;
  width: 100%;
  margin: 15px 0;
  background-color: ${({ theme }) => theme.colors.secondary};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    width: calc(100% + 72px);
  }
`;

const StyledDeleteButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.box};
  padding: 20px;
  width: 100%;
`;

const ArrowWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => rgba(theme.colors.accent, 0.1)};
  cursor: pointer;

  .icon {
    width: 9px;

    path {
      fill: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const Label = styled(Typography)`
  color: ${({ theme }) => theme.colors.accent};
`;
